import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Power, AlertCircle } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function AdminLocations() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState(50);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/locations").then((r) => setList(r.data));
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/locations", {
        name, lat: parseFloat(lat), lng: parseFloat(lng), radius_m: parseInt(radius) || 50, active: true,
      });
      toast.success("Lokasi ditambahkan");
      setName(""); setLat(""); setLng("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (loc) => {
    try {
      await api.put(`/locations/${loc.id}`, { ...loc, active: !loc.active });
      toast.success(loc.active ? "Lokasi dinonaktifkan" : "Lokasi diaktifkan");
      refresh();
    } catch { toast.error("Gagal"); }
  };

  const del = async (l) => {
    if (!window.confirm("Hapus lokasi?")) return;
    await api.delete(`/locations/${l.id}`);
    refresh();
  };

  const captureHere = () => {
    if (!navigator.geolocation) return toast.error("GPS tidak tersedia");
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); toast.success("Diambil"); },
      () => toast.error("Gagal")
    );
  };

  const activeCount = list.filter((l) => l.active).length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
      <p className="overline">Geofencing</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Lokasi Presensi</h1>

      {loading ? <PageLoader label="Memuat data lokasi..." /> : (<>
      <Card className="mt-6 p-4 border border-amber-300 bg-amber-50 shadow-none flex gap-3 items-start">
        <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Cara Kerja</p>
          <p>Jika ada minimal <b>1 lokasi aktif</b>, geofencing otomatis aktif: pegawai <b>wajib mengizinkan GPS</b> dan harus berada dalam radius salah satu lokasi aktif. Tanpa lokasi aktif, pegawai bisa absen tanpa GPS.</p>
          <p className="mt-1.5">Status saat ini: <Badge variant="outline" className={activeCount > 0 ? "border-green-600 text-green-700 bg-white" : "border-zinc-400 text-zinc-600 bg-white"} data-testid="geo-status">
            {activeCount > 0 ? `${activeCount} lokasi aktif — GEOFENCING AKTIF` : "Tidak ada lokasi aktif — GEOFENCING NONAKTIF"}
          </Badge></p>
        </div>
      </Card>

      <Card className="mt-4 p-6 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Tambah Lokasi</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nama</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="loc-name" /></div>
          <div><Label>Radius (meter)</Label><Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="loc-radius" /></div>
          <div><Label>Latitude</Label><Input value={lat} onChange={(e) => setLat(e.target.value)} required data-testid="loc-lat" /></div>
          <div><Label>Longitude</Label><Input value={lng} onChange={(e) => setLng(e.target.value)} required data-testid="loc-lng" /></div>
          <div className="md:col-span-2 flex gap-2">
            <Button type="button" variant="outline" onClick={captureHere} data-testid="btn-capture-loc">Ambil Lokasi Saya</Button>
            <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="btn-save-loc">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Simpan</>}
            </Button>
          </div>
        </form>
      </Card>

      <div className="mt-6 space-y-2">
        {list.length === 0 && <p className="text-sm text-zinc-500">Belum ada lokasi.</p>}
        {list.map((l) => (
          <Card key={l.id} className="p-3 border border-zinc-200 shadow-none flex justify-between items-center" data-testid={`loc-${l.id}`}>
            <div>
              <p className="font-semibold">{l.name}{" "}
                {l.active
                  ? <Badge variant="outline" className="ml-2 border-green-600 text-green-700">Aktif</Badge>
                  : <Badge variant="outline" className="ml-2 border-zinc-400 text-zinc-500">Nonaktif</Badge>}
              </p>
              <p className="text-xs text-zinc-500">{l.lat}, {l.lng} · Radius {l.radius_m}m</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Power className="w-4 h-4 text-zinc-500" />
                <Switch checked={l.active} onCheckedChange={() => toggleActive(l)} data-testid={`switch-active-${l.id}`} />
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(l)} data-testid={`btn-del-loc-${l.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
      </>)}
    </div>
  );
}
